import {I18nKeys} from '@multipass-i18n';
import {Button, FieldHelp, FieldLabel, Notice, TextInput} from '@multipass-ui-components';
import {syncModelWithSelectorOptions, usePublishEvent, useSyncModelWithSelector} from 'esp-js-react';
import {Clock, Mail} from 'lucide-react';
import * as React from 'react';

import {
    LoginLinkRequestedEvent,
    SESSION_STORE_ID,
    SessionEvents,
    SessionStoreModel,
    useT,
    useTReason,
} from '../../logic';
import {AuthHead, AuthLayout, AuthSwitchRow} from '../auth/AuthLayout';
import {EnterCodeView} from '../auth/EnterCodeView';
import {RouterLink} from '../shell/RouterLink';

const SignInForm: React.FunctionComponent<{session: SessionStoreModel}> = ({session}: {session: SessionStoreModel}) => {
    const [email, setEmail] = React.useState('');
    const publishEvent = usePublishEvent();
    const t = useT();
    const tReason = useTReason();
    const formError = session.formError;
    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            return;
        }
        publishEvent(SESSION_STORE_ID, SessionEvents.loginLinkRequested, {email: trimmedEmail} satisfies LoginLinkRequestedEvent);
    };
    return (
        <>
            <AuthHead title={t(I18nKeys.signIn.title)} sub={t(I18nKeys.signIn.subtitle)}/>
            {formError?.kind === 'unknownEmail' && (
                <Notice role="alert" className="mb-5">
                    {tReason(formError.code, formError.message)}{' '}
                    <RouterLink to="/signup" className="font-semibold text-link no-underline">{t(I18nKeys.signIn.switchLink)}</RouterLink>
                </Notice>
            )}
            {formError?.kind === 'sessionExpired' && (
                <Notice role="alert" className="mb-5">
                    {tReason(formError.code, formError.message)}
                </Notice>
            )}
            <form onSubmit={onSubmit}>
                <div className="mb-5">
                    <FieldLabel htmlFor="login-email">{t(I18nKeys.signIn.emailLabel)}</FieldLabel>
                    <TextInput
                        id="login-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                    />
                    <FieldHelp>{t(I18nKeys.signIn.emailHelp)}</FieldHelp>
                </div>
                <Button type="submit" size="lg" block>
                    <Mail size={20}/>
                    {t(I18nKeys.signIn.submit)}
                </Button>
                <p className="mt-5 flex items-center justify-center gap-2 text-caption text-text-muted">
                    <Clock size={16} className="flex-none"/>
                    {t(I18nKeys.signIn.linkExpiry, {minutes: 15})}
                </p>
            </form>
            <AuthSwitchRow>
                {t(I18nKeys.signIn.switchPrompt, {appName: t(I18nKeys.common.appName)})} <RouterLink to="/signup" className="font-bold text-link no-underline">{t(I18nKeys.signIn.switchLink)}</RouterLink>
            </AuthSwitchRow>
        </>
    );
};

export const LoginPage: React.FunctionComponent = () => {
    const session = useSyncModelWithSelector<SessionStoreModel, SessionStoreModel>(
        m => m,
        syncModelWithSelectorOptions<SessionStoreModel>().setStoreId(SESSION_STORE_ID)
    );
    return (
        <AuthLayout>
            {session.pendingEmail !== null
                ? <EnterCodeView session={session}/>
                : <SignInForm session={session}/>}
        </AuthLayout>
    );
};
